import React from "react";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import styles from "./billStyles";
import companyLogo from "../../../assets/images/frelit_logo svg.png";
import { CompanyInfo } from "../../../models/quotationData.model";
import { IProduct } from "../../../models/productData.model";

interface BillProps {
  bookedProducts: IProduct[];
  companyInfo: CompanyInfo;
  trasnportPrice?: number;
  discountPrice?: number;
}

const formatDate = (dateInput?: string): string => {
  const date = dateInput ? new Date(dateInput) : new Date();
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const Bill: React.FC<BillProps> = ({
  bookedProducts,
  companyInfo,
  trasnportPrice,
  discountPrice = 0,
}) => {
  const {
    name,
    mobileNumber,
    quotationDate,
    quotationNumber,
    quotationCreatedBy,
    QuotationCreatedByNumber,
    billingAddress,
    billingCity,
    billingState,
    billingPincode,
  } = companyInfo;

  const hasItemWiseDiscount = bookedProducts.some(
    (item: any) => item.discount && item.discount > 0,
  );

  const subTotal = bookedProducts.reduce(
    (sum, item) => sum + item.quantity * (item.price || 0),
    0,
  );

  const gstMap = new Map<number, { taxable: number; tax: number }>();
  let totalItemDiscounts = 0;

  bookedProducts.forEach((item: any) => {
    const originalAmount = item.quantity * (item.price || 0);
    const itemDiscount = item.discount || 0;
    totalItemDiscounts += itemDiscount;

    const taxableAmount = originalAmount - itemDiscount;
    const rate = item.gst || 0;
    const taxAmt = (taxableAmount * rate) / 100;

    if (!gstMap.has(rate)) {
      gstMap.set(rate, { taxable: 0, tax: 0 });
    }

    const entry = gstMap.get(rate)!;
    entry.taxable += taxableAmount;
    entry.tax += taxAmt;
  });

  const freight: number = Number.isFinite(parseInt(String(trasnportPrice), 10))
    ? parseInt(String(trasnportPrice), 10)
    : 0;

  const totalTax = Array.from(gstMap.values()).reduce(
    (acc, e) => acc + e.tax,
    0,
  );

  const finalTaxableAmount = subTotal - totalItemDiscounts;
  const grandTotal = finalTaxableAmount + totalTax + freight;

  const formatValue = (n: number): string => {
    const num = Number(n);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  const hasGST = bookedProducts.some((item) => item.gst && item.gst > 0);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.mainContent}>
          <View style={styles.headerRow}>
            <View style={styles.leftBlock}>
              <Image src={companyLogo} style={styles.logo} />
              <View style={styles.companyAndQuoteRow}>
                <View style={styles.companyBlock}>
                  <Text style={styles.companyName}>
                    Frelit Energy Pvt. Ltd.
                  </Text>
                  <Text style={styles.companyAddress}>
                    B-20, Bhagya Laxmi Society,{"\n"}Beside Aton Gym, Near
                    Darbar Chokdi, {"\n"}Manjalpur, Vadodara, Gujarat 390011
                  </Text>
                  <Text style={styles.gstInfo}>GSTIN/UIN: 24AADCF7701R1ZB</Text>
                </View>
                <View style={styles.quotationBox}>
                  <Text style={styles.quotationText}>Quotation</Text>
                </View>
              </View>
            </View>
          </View>

          <Text style={styles.sectionTitle}>Quotation To:</Text>
          <View style={styles.clientInfoHeader}>
            <View style={styles.clientInfo}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Company:</Text>
                <Text style={styles.infoValue}>{name}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Phone:</Text>
                <Text style={styles.infoValue}>{mobileNumber}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Address:</Text>
                <Text style={styles.infoValue}>
                  {`${billingAddress ?? ""}, ${billingCity ?? ""}, ${billingState ?? ""}, ${billingPincode ?? ""}`}
                </Text>
              </View>
            </View>
            <View style={styles.metaContainer}>
              <View style={styles.infoRow}>
                <Text style={styles.smallInfo}>Quotation No:</Text>
                <Text style={styles.infoValue}>{quotationNumber}</Text>
              </View>
              {quotationDate && (
                <View style={styles.infoRow}>
                  <Text style={styles.smallInfo}>Quotation Date:</Text>
                  <Text style={styles.infoValue}>
                    {formatDate(quotationDate.toString())}
                  </Text>
                </View>
              )}
              {quotationCreatedBy && (
                <View style={styles.infoRow}>
                  <Text style={styles.smallInfo}>Created By:</Text>
                  <Text style={styles.infoValue}>{quotationCreatedBy}</Text>
                </View>
              )}
              <View style={styles.infoRow}>
                <Text style={styles.smallInfo}>Contact No:</Text>
                <Text style={styles.infoValue}>{QuotationCreatedByNumber}</Text>
              </View>
            </View>
          </View>

          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableHeaderText, styles.slNoCell]}>
                SR NO.
              </Text>
              <Text style={[styles.tableHeaderText, styles.descriptionCell]}>
                ITEM DESCRIPTION
              </Text>
              <Text style={[styles.tableHeaderText, styles.hsnCell]}>HSN</Text>
              <Text style={[styles.tableHeaderText, styles.qtyCell]}>
                QTY (UNIT)
              </Text>
              <Text style={[styles.tableHeaderText, styles.rateCell]}>
                RATE (INR)
              </Text>
              {hasItemWiseDiscount && (
                <Text
                  style={[
                    styles.tableHeaderText,
                    { width: "10%", textAlign: "center" },
                  ]}
                >
                  DISC (INR)
                </Text>
              )}
              <Text style={[styles.tableHeaderText, styles.totalCell]}>
                TOTAL (INR)
              </Text>
            </View>

            {bookedProducts.map((item: any, index) => {
              const amount = item.quantity * (item.price || 0);
              const itemDisc = item.discount || 0;
              const lineTotal = amount - itemDisc;
              return (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, styles.slNoCell]}>
                    {index + 1}
                  </Text>
                  <Text style={[styles.tableCellDesc, styles.descriptionCell]}>
                    {item.productName}
                  </Text>
                  <Text style={[styles.tableCell, styles.hsnCell]}>
                    {item.hsn}
                  </Text>
                  <Text style={[styles.tableCell, styles.qtyCell]}>
                    {item.quantity} {item.unit || ""}
                  </Text>
                  <Text style={[styles.tableCell, styles.rateCell]}>
                    {item.price.toLocaleString()}
                  </Text>
                  {hasItemWiseDiscount && (
                    <Text
                      style={[
                        styles.tableCell,
                        { width: "10%", textAlign: "center" },
                      ]}
                    >
                      {itemDisc > 0 ? itemDisc.toLocaleString() : "—"}
                    </Text>
                  )}
                  <Text style={[styles.tableCell, styles.totalCell]}>
                    {lineTotal.toLocaleString()}
                  </Text>
                </View>
              );
            })}
          </View>

          <View style={styles.totals}>
            <View style={[styles.totalRow, { padding: 4 }]}>
              <Text style={styles.totalLabel}>Sub-total:</Text>
              <Text style={styles.totalValue}>INR {formatValue(subTotal)}</Text>
            </View>

            {totalItemDiscounts > 0 && (
              <View style={[styles.totalRow, { padding: 4 }]}>
                <Text style={styles.totalLabel}>Discount:</Text>
                <Text style={styles.totalValue}>
                  - INR {formatValue(totalItemDiscounts)}
                </Text>
              </View>
            )}

            {hasGST &&
              Array.from(gstMap.entries()).map(([rate, { tax }]) => (
                <View key={rate} style={[styles.totalRow, { padding: 4 }]}>
                  <Text style={styles.totalLabel}>{`Tax (${rate}%):`}</Text>
                  <Text style={styles.totalValue}>INR {formatValue(tax)}</Text>
                </View>
              ))}

            {freight > 0 && (
              <View style={[styles.totalRow, { padding: 4 }]}>
                <Text style={styles.totalLabel}>Freight:</Text>
                <Text style={styles.totalValue}>
                  INR {formatValue(freight)}
                </Text>
              </View>
            )}

            <View
              style={[
                styles.totalRow,
                { backgroundColor: "#0057B7", color: "#fff", padding: 6 },
              ]}
            >
              <Text style={[styles.totalLabel, { fontWeight: "bold" }]}>
                Total:
              </Text>
              <Text style={[styles.totalValue, { fontWeight: "bold" }]}>
                INR {formatValue(grandTotal)}
              </Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: "row",
              marginTop: 6,
              justifyContent: "space-between",
            }}
          >
            <View style={styles.terms}>
              <Text style={styles.termsTitle}>TERMS AND CONDITIONS</Text>
              <Text style={styles.termsItem}>
                - Payment Terms: 100% Advance
              </Text>
              <Text style={styles.termsItem}>
                - Validity: 15 days from date of issue.
              </Text>
              <Text style={styles.termsItem}>
                - Delivery: Within 5–7 working days after order confirmation.
              </Text>
              <Text style={styles.termsItem}>
                - Freight charges approximate and may vary at dispatch.
              </Text>
            </View>
            <View style={{ width: 250 }}>
              <Text style={styles.bankTitle}>PAYMENT BANK DETAILS</Text>
              <View style={styles.bankDetailsTable}>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Account No.:</Text>
                  <Text style={styles.bankValue}>922030048197928</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Account Name:</Text>
                  <Text style={styles.bankValue}>Frelit Energy Pvt. Ltd.</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>IFSC:</Text>
                  <Text style={styles.bankValue}>UTIB0002999</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Bank Name:</Text>
                  <Text style={styles.bankValue}>Axis Bank Ltd.</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Branch Name:</Text>
                  <Text style={styles.bankValue}>Maneja</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footerContainer} fixed>
          <View style={styles.footer}>
            <Text style={styles.footerText}>www.frelitenergy.com</Text>
            <Text style={styles.footerText}>079-41056633</Text>
            <Text style={styles.footerText}>sales@frelitenergy.com</Text>
          </View>
          <View style={styles.subFooter}>
            <Text style={styles.jurisdictionText}>
              Subject to Vadodara Jurisdiction
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default Bill;
